<template>
  <div class="user-payment-registration">
    <!-- Шаг 1: Выбор типа оплаты -->
    <div v-if="!paymentForm.payment_type_id" class="payment-type-selection">
      <h3 class="selection-title">Что вы хотите оплатить?</h3>
      
      <div class="payment-types-list">
        <div
          v-for="paymentType in teamPaymentTypes"
          :key="paymentType.id"
          class="payment-type-card"
          @click="selectPaymentType(paymentType.id)"
        >
          <!-- Содержимое карточки -->
          <div class="payment-card-content">
            <!-- Название и статус на одной строке -->
            <div class="payment-title-row">
              <h4 class="payment-title">{{ paymentType.name }}</h4>
              <div 
                class="payment-status-badge" 
                :class="getStatusBadgeClass(getPaymentStatus(paymentType.id).status)"
                :style="{ backgroundColor: getStatusBadgeColor(getPaymentStatus(paymentType.id).status) }"
              >
                <span class="badge-icon">
                  <CheckCircleOutlined v-if="getPaymentStatus(paymentType.id).status === 'completed'" />
                  <ClockCircleOutlined v-else-if="getPaymentStatus(paymentType.id).status === 'pending'" />
                  <CloseCircleOutlined v-else-if="getPaymentStatus(paymentType.id).status === 'unpaid'" />
                  <SafetyCertificateOutlined v-else-if="getPaymentStatus(paymentType.id).status === 'exempt'" />
                </span>
                <span class="badge-text">{{ getStatusBadgeText(getPaymentStatus(paymentType.id).status) }}</span>
              </div>
            </div>
            
            <!-- Детали платежа -->
            <div class="payment-details">
              <div class="payment-info-row" v-if="paymentType.description">
                <FileOutlined class="payment-icon" />
                <span class="payment-text payment-description-text">{{ paymentType.description }}</span>
              </div>
              <div class="payment-info-row" v-if="paymentType.category">
                <TagOutlined class="payment-icon" />
                <span class="payment-text">{{ getTypeCategoryLabel(paymentType.category) }}</span>
              </div>
              <div class="payment-info-row" v-if="paymentType.type === 'subscription' && paymentType.frequency">
                <ClockCircleOutlined class="payment-icon" />
                <span class="payment-text">{{ getFrequencyLabel(paymentType.frequency, paymentType.custom_frequency_days) }}</span>
              </div>
              
              <!-- Информация об освобождении -->
              <div v-if="getPaymentStatus(paymentType.id).status === 'exempt' && getPaymentStatus(paymentType.id).exemptUntilPeriod" 
                   class="payment-info-row" 
                   style="background: #e6f7ff; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <SafetyCertificateOutlined class="payment-icon" style="color: #1890ff;" />
                <div style="display: flex; flex-direction: column;">
                  <span class="payment-text" style="color: #1890ff; font-weight: 500;">
                    Освобождение до {{ formatExemptUntilPeriod(getPaymentStatus(paymentType.id).exemptUntilPeriod) }}
                  </span>
                  <span v-if="getPaymentStatus(paymentType.id).exemptionReason" style="color: #8c8c8c; font-size: 11px; margin-top: 2px;">
                    Причина: {{ getPaymentStatus(paymentType.id).exemptionReason }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Сумма платежа -->
            <div class="payment-amount-section">
              <div class="amount-label">Сумма:</div>
              <div class="amount-value">
                <span v-if="paymentType.amount" class="amount-fixed">{{ formatAmount(paymentType.amount) }} ₽</span>
                <span v-else class="amount-flexible">Произвольная</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Шаг 2: Форма регистрации оплаты -->
    <div v-else class="payment-form-container">
      <div class="form-header">
        <a-button type="text" @click="resetPaymentType" class="back-button">
          <ArrowLeftOutlined />
          Назад
        </a-button>
        <div class="selected-type-info">
          <h3>{{ selectedPaymentType?.name }}</h3>
        </div>
      </div>

      <!-- Информация о выбранном типе оплаты (в стиле карточки из списка) -->
      <div v-if="selectedPaymentType" class="selected-payment-card">
        <!-- Детали платежа -->
        <div class="payment-details">
          <div class="payment-info-row" v-if="selectedPaymentType.description">
            <FileOutlined class="payment-icon" />
            <span class="payment-text payment-description-text">{{ selectedPaymentType.description }}</span>
          </div>
          <div class="payment-info-row" v-if="selectedPaymentType.category">
            <TagOutlined class="payment-icon" />
            <span class="payment-text">{{ getTypeCategoryLabel(selectedPaymentType.category) }}</span>
          </div>
          <div class="payment-info-row" v-if="selectedPaymentType.type === 'subscription' && selectedPaymentType.frequency">
            <ClockCircleOutlined class="payment-icon" />
            <span class="payment-text">{{ getFrequencyLabel(selectedPaymentType.frequency, selectedPaymentType.custom_frequency_days) }}</span>
          </div>
        </div>

        <!-- Сумма платежа -->
        <div class="payment-amount-section">
          <div class="amount-label">Сумма:</div>
          <div class="amount-value">
            <span v-if="selectedPaymentType.amount" class="amount-fixed">{{ formatAmount(selectedPaymentType.amount) }} ₽</span>
            <span v-else class="amount-flexible">Произвольная</span>
          </div>
        </div>
      </div>

      <a-form
        :model="paymentForm"
        layout="vertical"
        @submit.prevent="submitPayment"
        class="payment-form"
      >

      <a-form-item label="Месяц оплаты" v-if="selectedPaymentType && selectedPaymentType.type === 'subscription'">
        <a-date-picker
          v-model:value="paymentForm.payment_month"
          picker="month"
          format="MMMM YYYY"
          placeholder="Выберите месяц"
          :disabled="loading"
          style="width: 100%"
          :locale="ruRU"
        />
      </a-form-item>

      <a-form-item label="Сумма оплаты (руб.)">
        <a-input-number
          v-model:value="paymentForm.amount"
          :min="0"
          :precision="2"
          placeholder="Введите сумму оплаты"
          :disabled="loading"
          style="width: 100%"
          :formatter="formatInputAmount"
          :parser="parseInputAmount"
        />
      </a-form-item>

      <a-form-item label="Комментарий">
        <a-textarea
          v-model:value="paymentForm.comment"
          placeholder="Дополнительная информация об оплате (необязательно)"
          :rows="3"
          :disabled="loading"
        />
      </a-form-item>

      <a-form-item label="Квитанция об оплате">
        <div class="receipt-upload">
          <input
            type="file"
            ref="fileInput"
            accept="image/*,.pdf"
            style="display: none"
            @change="handleFileUpload"
          />
          <a-button 
            type="dashed"
            @click="$refs.fileInput.click()"
            :loading="uploadingFile"
            :disabled="loading"
            block
          >
            <UploadOutlined />
            {{ uploadingFile ? 'Загрузка...' : 'Прикрепить квитанцию' }}
          </a-button>
          <div class="file-hint">
            Поддерживаемые форматы: JPEG, PNG, GIF, HEIC, PDF (макс. 10 МБ)
          </div>
          <div v-if="paymentForm.receipt_url" class="receipt-preview">
            <img 
              v-if="isImageFile(paymentForm.receipt_url)" 
              :src="paymentForm.receipt_url" 
              alt="Квитанция"
              class="receipt-image"
            />
            <div v-else class="receipt-file">
              <FileOutlined />
              <span>Файл загружен</span>
            </div>
            <a-button 
              type="text" 
              danger 
              size="small"
              @click="removeReceipt"
              :disabled="loading"
            >
              <DeleteOutlined />
            </a-button>
          </div>
        </div>
      </a-form-item>

      <a-form-item>
        <a-button 
          type="primary" 
          html-type="submit" 
          :loading="submitting"
          :disabled="!isFormValid"
          block
          size="large"
        >
          {{ submitting ? 'Отправка...' : 'Зарегистрировать оплату' }}
        </a-button>
      </a-form-item>

      <a-divider style="margin: 24px 0;">
        <span style="color: #8c8c8c; font-size: 14px;">или</span>
      </a-divider>

      <a-form-item>
        <a-button 
          type="default" 
          @click="openExemptionRequestModal"
          :loading="submitting"
          block
          size="large"
          style="height: auto; min-height: 48px; white-space: normal; padding: 12px 16px;"
        >
          <div style="display: flex; flex-direction: column; align-items: center; gap: 4px;">
            <span style="font-size: 16px; font-weight: 500;">Запросить освобождение</span>
            <span style="font-size: 12px; color: #8c8c8c; font-weight: normal;">от оплаты</span>
          </div>
        </a-button>
        <div style="color: #8c8c8c; font-size: 12px; margin-top: 8px; text-align: center;">
          Если вы временно не можете оплатить (травма, отпуск и т.д.)
        </div>
      </a-form-item>
    </a-form>
    </div>

    <!-- Модальное окно запроса освобождения -->
    <a-modal
      v-model:open="exemptionRequestModalVisible"
      title="Запрос освобождения от оплаты"
      :confirm-loading="requestingExemption"
      @ok="requestExemption"
      @cancel="cancelExemptionRequest"
      ok-text="Отправить запрос"
      cancel-text="Отмена"
    >
      <div v-if="selectedPaymentType">
        <p><strong>Тип оплаты:</strong> {{ selectedPaymentType.name }}</p>
        <p v-if="selectedPaymentType.amount"><strong>Сумма:</strong> {{ selectedPaymentType.amount }} ₽</p>
        
        <a-alert
          message="Информация"
          description="Ваш запрос будет отправлен администратору команды на рассмотрение. После одобрения вы будете освобождены от оплаты на указанный период."
          type="info"
          show-icon
          style="margin-bottom: 16px;"
        />
        
        <a-form layout="vertical">
          <a-form-item label="Причина запроса" required>
            <a-textarea
              v-model:value="exemptionRequestForm.reason"
              :rows="4"
              placeholder="Например: Травма, не могу участвовать в играх; Отпуск за границей; Семейные обстоятельства..."
            />
          </a-form-item>
          
          <a-form-item v-if="selectedPaymentType.type === 'subscription'" label="Запросить освобождение до" required>
            <a-select
              v-model:value="exemptionRequestForm.exemptUntilPeriod"
              placeholder="Выберите последний месяц"
            >
              <a-select-option
                v-for="month in futureMonths"
                :key="month.value"
                :value="month.value"
              >
                {{ month.label }}
              </a-select-option>
            </a-select>
            <div style="color: #8c8c8c; font-size: 12px; margin-top: 4px;">
              Период, на который вы запрашиваете освобождение
            </div>
          </a-form-item>
        </a-form>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { api } from '../services/api';
import { Form, FormItem, Select, DatePicker, InputNumber, Input, Button, Spin, Tag, Card } from 'ant-design-vue';
import { TeamOutlined } from '@ant-design/icons-vue';
import { 
  UploadOutlined, 
  DeleteOutlined, 
  FileOutlined,
  DollarOutlined,
  ArrowLeftOutlined,
  TagOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import localeData from 'dayjs/plugin/localeData';
import ruRU from 'ant-design-vue/es/date-picker/locale/ru_RU';

// Настройка локализации для dayjs
dayjs.extend(localeData);
dayjs.locale('ru');

export default {
  name: 'UserPaymentRegistration',
  props: {
    teamId: {
      type: Number,
      required: true
    }
  },
  components: {
    'a-form': Form,
    'a-form-item': FormItem,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-date-picker': DatePicker,
    'a-input-number': InputNumber,
    'a-textarea': Input.TextArea,
    'a-button': Button,
    'a-spin': Spin,
    'a-tag': Tag,
    'a-card': Card,
    UploadOutlined,
    DeleteOutlined,
    FileOutlined,
    TeamOutlined,
    DollarOutlined,
    ArrowLeftOutlined,
    TagOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SafetyCertificateOutlined
  },
  data() {
    return {
      ruRU,
      paymentForm: {
        payment_type_id: null,
        payment_month: null,
        amount: null,
        comment: '',
        receipt_url: ''
      },
      exemptionRequestForm: {
        reason: '',
        exemptUntilPeriod: ''
      },
      currentTeamId: null,
      currentTeamName: '',
      teamPaymentTypes: [],
      paymentStatuses: {}, // Статусы оплаты для каждого типа: { paymentTypeId: { status, amount, date } }
      loading: false,
      submitting: false,
      uploadingFile: false,
      exemptionRequestModalVisible: false,
      requestingExemption: false
    };
  },
  computed: {
    selectedPaymentType() {
      if (!this.paymentForm.payment_type_id) return null;
      return this.teamPaymentTypes.find(pt => pt.id === this.paymentForm.payment_type_id);
    },
    isFormValid() {
      return (this.teamId || this.currentTeamId) && 
             this.paymentForm.payment_type_id && 
             this.paymentForm.amount && 
             this.paymentForm.amount > 0;
    },
    futureMonths() {
      const months = [];
      const currentDate = new Date();
      
      // Добавляем текущий + 12 будущих месяцев
      for (let i = 0; i <= 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('ru-RU', { 
          month: 'long', 
          year: 'numeric' 
        });
        months.push({ value, label });
      }
      
      return months;
    }
  },
  methods: {
    // Получить статус оплаты для типа платежа
    getPaymentStatus(paymentTypeId) {
      return this.paymentStatuses[paymentTypeId] || { status: 'unpaid' };
    },

    // Получить цвет бейджа статуса
    getStatusBadgeColor(status) {
      const colors = {
        completed: '#52c41a',  // Зеленый
        pending: '#faad14',     // Желтый
        unpaid: '#8c8c8c',      // Серый
        exempt: '#1890ff'       // Синий
      };
      return colors[status] || '#8c8c8c';
    },

    // Получить текст статуса
    getStatusBadgeText(status) {
      const texts = {
        completed: 'Оплачено',
        pending: 'Ожидает проверки',
        unpaid: 'Не оплачено',
        exempt: 'Освобождён'
      };
      return texts[status] || 'Не оплачено';
    },

    // Получить класс бейджа статуса
    getStatusBadgeClass(status) {
      return `status-${status}`;
    },

    async loadUserTeams() {
      try {
        this.loading = true;
        const subscriptionStatus = await api.getSubscriptionStatus();
        const activeTeams = subscriptionStatus.filter(team => team.subscription_enabled);
        
        if (activeTeams.length > 0) {
          // Берем первую активную команду
          const firstTeam = activeTeams[0];
          this.currentTeamId = firstTeam.team_id;
          this.currentTeamName = firstTeam.team_name;
          
          // Показываем информацию о выбранной команде
          console.log(`Загружаем типы оплат для команды: ${firstTeam.team_name} (ID: ${firstTeam.team_id})`);
          
          await this.loadTeamPaymentTypes(this.currentTeamId);
        } else {
          console.log('Нет активных команд с включенными абонементами');
        }
        
        // Устанавливаем значения по умолчанию после загрузки команд
        this.setDefaultValues();
      } catch (error) {
        console.error('Error loading user teams:', error);
        this.$message.error('Ошибка при загрузке команд');
      } finally {
        this.loading = false;
      }
    },

    async loadTeamPaymentTypes(teamId) {
      try {
        const response = await api.getPublicTeamPaymentTypes(teamId);
        // API возвращает массив напрямую
        this.teamPaymentTypes = Array.isArray(response) ? response : [];
        
        // Загружаем информацию о команде
        await this.loadTeamInfo(teamId);
        
        // Загружаем статусы оплаты для каждого типа
        await this.loadPaymentStatuses(teamId);
        
        // Не выбираем автоматически тип оплаты - пусть пользователь выберет сам
        // Это делает интерфейс более понятным при наличии нескольких типов оплат
      } catch (error) {
        console.error('Error loading team payment types:', error);
        this.$message.error('Ошибка при загрузке типов оплат');
      }
    },

    async loadPaymentStatuses(teamId) {
      try {
        // Загружаем историю платежей и статусы освобождений
        const [userPayments, exemptionStatuses] = await Promise.all([
          api.getUserPaymentHistory(teamId),
          api.getUserExemptionStatus(teamId)
        ]);
        
        console.log('User payment history loaded:', userPayments);
        console.log('Exemption statuses loaded:', exemptionStatuses);
        
        // Для каждого типа оплаты определяем статус
        const now = dayjs();
        const currentMonth = now.format('YYYY-MM');
        const nextMonth = now.add(1, 'month').format('YYYY-MM');
        const currentDay = now.date();
        
        for (const paymentType of this.teamPaymentTypes) {
          try {
            // Сначала проверяем освобождение
            const exemptionStatus = exemptionStatuses[paymentType.id];
            if (exemptionStatus && exemptionStatus.is_exempt) {
              this.paymentStatuses[paymentType.id] = {
                status: 'exempt',
                exemptionReason: exemptionStatus.exemption_reason,
                exemptUntilPeriod: exemptionStatus.exempt_until_period
              };
              continue;
            }
            
            // Проверяем, есть ли оплата для этого типа
            let payment;
            
            if (paymentType.type === 'subscription') {
              // Для абонементов нужно проверить правильный месяц
              // Если текущий день <= дня оплаты, то проверяем платежи за следующий месяц
              // Если текущий день > дня оплаты, то проверяем платежи за текущий месяц
              let monthToCheck = currentMonth;
              if (currentDay <= paymentType.payment_deadline_day) {
                // Payment deadline hasn't passed yet this month (or it's the deadline day), so we check for next month's payment
                monthToCheck = nextMonth;
              }
              
              payment = userPayments.find(p => 
                p.payment_type_id === paymentType.id && 
                dayjs(p.payment_date).format('YYYY-MM') === monthToCheck
              );
            } else {
              // Для разовых - проверяем наличие любой оплаты
              payment = userPayments.find(p => 
                p.payment_type_id === paymentType.id
              );
            }

            if (payment) {
              this.paymentStatuses[paymentType.id] = {
                status: payment.status,
                amount: payment.amount,
                date: payment.payment_date
              };
            } else {
              this.paymentStatuses[paymentType.id] = {
                status: 'unpaid'
              };
            }
          } catch (error) {
            console.error(`Error loading status for payment type ${paymentType.id}:`, error);
            this.paymentStatuses[paymentType.id] = { status: 'unpaid' };
          }
        }
        
        console.log('Payment statuses loaded:', this.paymentStatuses);
      } catch (error) {
        console.error('Error loading payment statuses:', error);
      }
    },

    async loadTeamInfo(teamId) {
      try {
        // Получаем информацию о команде из subscriptionStatus
        const subscriptionStatus = await api.getSubscriptionStatus();
        const teamInfo = subscriptionStatus.find(team => team.team_id === teamId);
        if (teamInfo) {
          this.currentTeamName = teamInfo.team_name;
          console.log(`Загружена информация о команде: ${teamInfo.team_name} (ID: ${teamId})`);
        }
      } catch (error) {
        console.error('Error loading team info:', error);
        this.currentTeamName = 'Команда';
      }
    },

    selectPaymentType(paymentTypeId) {
      this.paymentForm.payment_type_id = paymentTypeId;
      this.onPaymentTypeChange(paymentTypeId);
    },

    resetPaymentType() {
      this.paymentForm.payment_type_id = null;
      this.paymentForm.amount = null;
      this.paymentForm.payment_month = null;
      this.paymentForm.comment = '';
      this.paymentForm.receipt_url = '';
    },

    getTypeColor(type) {
      const colors = {
        subscription: 'blue',
        one_time: 'green',
        special: 'orange'
      };
      return colors[type] || 'default';
    },

    getTypeLabel(type) {
      const labels = {
        subscription: 'Периодическая',
        one_time: 'Разовая',
        special: 'Специальная'
      };
      return labels[type] || type;
    },

    setDefaultValues() {
      // Устанавливаем следующий месяц за текущим для абонементов
      const nextMonth = dayjs().add(1, 'month');
      this.paymentForm.payment_month = nextMonth;

      // Не автоматически выбираем тип оплаты - пусть пользователь выберет сам
    },

    resetForm() {
      // Сохраняем выбранный тип оплаты, если он есть
      const currentPaymentTypeId = this.paymentForm.payment_type_id;
      
      Object.assign(this.paymentForm, {
        payment_type_id: currentPaymentTypeId, // Сохраняем выбранный тип
        payment_month: null,
        amount: null,
        comment: '',
        receipt_url: ''
      });
      
      // Устанавливаем значения по умолчанию
      this.setDefaultValues();
    },



    onPaymentTypeChange(paymentTypeId) {
      const paymentType = this.teamPaymentTypes.find(pt => pt.id === paymentTypeId);
      if (paymentType && paymentType.amount) {
        this.paymentForm.amount = paymentType.amount;
      } else {
        this.paymentForm.amount = null;
      }
    },

    getTypeCategoryLabel(category) {
      const categories = {
        'membership': 'Членские взносы',
        'equipment': 'Экипировка',
        'tournament': 'Турниры',
        'training': 'Тренировки',
        'penalty': 'Штрафы',
        'other': 'Прочее'
      };
      return categories[category] || category;
    },

    getFrequencyLabel(frequency, customDays) {
      const frequencies = {
        'monthly': 'Ежемесячно',
        'quarterly': 'Ежеквартально',
        'yearly': 'Ежегодно',
        'custom': `Каждые ${customDays} дней`
      };
      return frequencies[frequency] || frequency;
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return amount.toLocaleString('ru-RU');
    },

    formatExemptUntilPeriod(period) {
      if (!period) return '';
      const [year, month] = period.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('ru-RU', {
        month: 'long',
        year: 'numeric'
      });
    },

    formatInputAmount(value) {
      if (!value) return '';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    parseInputAmount(value) {
      if (!value) return null;
      return value.toString().replace(/\s/g, '');
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        this.uploadingFile = true;
        const formData = new FormData();
        formData.append('photo', file);
        
        const response = await api.uploadReceipt(formData);
        this.paymentForm.receipt_url = response.receipt_url;
        this.$message.success('Квитанция загружена');
      } catch (error) {
        console.error('Error uploading file:', error);
        this.$message.error('Ошибка при загрузке файла');
      } finally {
        this.uploadingFile = false;
      }
    },

    removeReceipt() {
      this.paymentForm.receipt_url = '';
    },

    isImageFile(url) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    },

    getCurrentTeamName() {
      if (!this.currentTeamId) return '';
      
      // Получаем название команды из subscriptionStatus
      // Для этого нужно сохранить информацию о командах
      return this.currentTeamName || 'Команда';
    },

    // Открыть модальное окно запроса освобождения
    openExemptionRequestModal() {
      this.exemptionRequestForm.reason = '';
      this.exemptionRequestForm.exemptUntilPeriod = '';
      this.exemptionRequestModalVisible = true;
    },

    // Отправить запрос на освобождение
    async requestExemption() {
      if (!this.exemptionRequestForm.reason || this.exemptionRequestForm.reason.trim() === '') {
        this.$message.warning('Укажите причину запроса освобождения');
        return;
      }

      if (this.selectedPaymentType.type === 'subscription' && !this.exemptionRequestForm.exemptUntilPeriod) {
        this.$message.warning('Выберите период, до которого запрашиваете освобождение');
        return;
      }

      try {
        this.requestingExemption = true;

        await api.requestPaymentExemption(this.teamId || this.currentTeamId, this.paymentForm.payment_type_id, {
          exemption_reason: this.exemptionRequestForm.reason,
          exempt_until_period: this.exemptionRequestForm.exemptUntilPeriod
        });

        this.$message.success('Запрос на освобождение отправлен администратору');
        this.exemptionRequestModalVisible = false;

        // Обновляем статусы оплат
        await this.loadPaymentStatuses(this.teamId || this.currentTeamId);
        
        // Возвращаемся к списку типов оплат
        this.resetPaymentType();
      } catch (error) {
        console.error('Error requesting exemption:', error);
        this.$message.error('Ошибка при отправке запроса на освобождение');
      } finally {
        this.requestingExemption = false;
      }
    },

    // Отменить запрос освобождения
    cancelExemptionRequest() {
      this.exemptionRequestModalVisible = false;
      this.exemptionRequestForm.reason = '';
      this.exemptionRequestForm.exemptUntilPeriod = '';
    },

    async submitPayment() {
      try {
        this.submitting = true;
        
        const paymentData = {
          team_id: this.teamId || this.currentTeamId, // Используем prop, если доступен
          payment_type_id: this.paymentForm.payment_type_id,
          amount: this.paymentForm.amount,
          comment: this.paymentForm.comment,
          receipt_url: this.paymentForm.receipt_url
        };

        // Добавляем дату в зависимости от типа оплаты
        if (this.selectedPaymentType && this.selectedPaymentType.type === 'subscription') {
          paymentData.payment_month = this.paymentForm.payment_month.format('YYYY-MM');
        }

        await api.registerPlayerPayment(paymentData);
        
        this.$message.success('Оплата зарегистрирована и отправлена на проверку администратору');
        
        // Перезагружаем статусы оплаты
        await this.loadPaymentStatuses(this.teamId || this.currentTeamId);
        
        // Small delay to ensure backend is fully updated before reloading
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Reload subscription status in main app to update warning message
        if (this.$root && this.$root.loadSubscriptionStatus) {
          await this.$root.loadSubscriptionStatus();
        }
        
        // Reset form
        this.paymentForm = {
          payment_type_id: null,
          payment_month: null,
          amount: null,
          comment: '',
          receipt_url: ''
        };
        
        // Reload form with default values
        this.setDefaultValues();
        
      } catch (error) {
        console.error('Error submitting payment:', error);
        this.$message.error('Ошибка при регистрации оплаты');
      } finally {
        this.submitting = false;
      }
    }
  },
  watch: {
    teamId: {
      immediate: true,
      handler(newTeamId, oldTeamId) {
        if (newTeamId && newTeamId !== oldTeamId) {
          console.log(`Команда изменилась с ${oldTeamId} на ${newTeamId}`);
          this.currentTeamId = newTeamId;
          this.loadTeamPaymentTypes(newTeamId);
          // Сбрасываем форму только при смене команды, а не при первой загрузке
          if (oldTeamId !== undefined) {
            this.resetForm();
          }
        }
      }
    }
  },
  async mounted() {
    // Если teamId передан как prop, используем его
    if (this.teamId) {
      this.currentTeamId = this.teamId;
      await this.loadTeamPaymentTypes(this.teamId);
    } else {
      // Иначе загружаем команды пользователя
      await this.loadUserTeams();
    }
    this.setDefaultValues();
  }
};
</script>

<style scoped>
.user-payment-registration {
  padding: 16px;
}

.registration-header {
  margin-bottom: 24px;
}

.registration-header h4 {
  margin-bottom: 8px;
  color: #1890ff;
  font-size: 16px;
}

.registration-description {
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.current-team-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.team-payment-types-count {
  color: #666;
  font-size: 12px;
}

.payment-form {
  margin-bottom: 24px;
}

.payment-type-info {
  margin-top: 8px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border-left: 3px solid #1890ff;
}

.type-description {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 14px;
}

.type-category, .type-frequency {
  margin: 0 0 4px 0;
  color: #666;
  font-size: 12px;
}


.receipt-upload {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  line-height: 1.4;
}

.receipt-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
}

.receipt-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.receipt-file {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
}

/* Новые стили для улучшенного интерфейса (в стиле карточек событий) */
.payment-type-selection {
  padding: 16px;
}

.selection-title {
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin: 0 0 8px 0;
}

.selection-description {
  font-size: 14px;
  color: #8c8c8c;
  margin: 0 0 24px 0;
}

.payment-types-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-type-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  cursor: pointer;
}

.payment-type-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #1890ff;
}

.payment-card-content {
  padding: 16px;
}

.payment-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.payment-title {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  margin: 0;
  flex: 1;
  min-width: 150px;
}

.payment-status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
}

.payment-status-badge.status-completed {
  background-color: #52c41a;
}

.payment-status-badge.status-pending {
  background-color: #faad14;
}

.payment-status-badge.status-unpaid {
  background-color: #8c8c8c;
}

.payment-status-badge.status-exempt {
  background-color: #1890ff;
}

.badge-icon {
  font-size: 12px;
  display: flex;
  align-items: center;
}

.badge-text {
  font-weight: 600;
  font-size: 11px;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.payment-info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.payment-icon {
  font-size: 16px;
  color: #8c8c8c;
  flex-shrink: 0;
}

.payment-text {
  font-size: 14px;
  color: #595959;
  line-height: 1.5;
  flex: 1;
}

.payment-description-text {
  font-style: italic;
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 6px;
  margin-left: -8px;
}

.payment-amount-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 8px;
  border: 1px solid #bae7ff;
}

.amount-label {
  font-size: 14px;
  color: #595959;
  font-weight: 500;
}

.amount-value {
  display: flex;
  align-items: center;
}

.amount-fixed {
  font-size: 20px;
  font-weight: 700;
  color: #52c41a;
}

.amount-flexible {
  font-size: 14px;
  color: #8c8c8c;
  font-style: italic;
}

.payment-form-container {
  padding: 0px;
}

.form-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
}

.back-button {
  padding: 4px 8px;
  color: #1890ff;
}

.selected-type-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-type-info h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #262626;
}

.selected-payment-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
  padding: 16px;
  margin-bottom: 24px;
}

/* Адаптация для мобильных устройств */
@media (max-width: 768px) {
  .payment-type-selection {
    padding: 0px;
  }

  .selection-title {
    font-size: 18px;
  }

  .payment-types-list {
    gap: 12px;
  }

  .payment-card-content {
    padding: 12px;
  }

  .payment-title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .payment-title {
    font-size: 16px;
    width: 100%;
  }

  .payment-status-badge {
    align-self: flex-start;
  }

  .payment-amount-section {
    padding: 10px;
  }

  .amount-fixed {
    font-size: 18px;
  }

  .form-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .selected-type-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .selected-payment-card {
    padding: 12px;
  }
}
</style> 